import { useCallback, useEffect, useMemo, useState } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import { useSubscription } from 'observable-hooks';
import { BehaviorSubject, finalize, map, of, switchMap, tap } from 'rxjs';

import { localStorageManager } from '../localStorageManager';
import { getCountdownTimer$ } from '../useCountdownTimer$/useCountdownTimer$';

const GUARD_SECONDS = 60;

export const usePersistentCountdownGuard = (
	key: string,
	guardSeconds = GUARD_SECONDS
) => {
	const guardLSManager = useMemo(() => localStorageManager<number>(key), [key]);
	const [countdownGuardValue, setCountdownGuardValue] = useState<
		number | null | false
	>(null);

	const initCountdownGuard$ = useMemo(
		() => new BehaviorSubject<Dayjs | false | null>(null),
		[]
	);

	const countdownGuardValue$ = useMemo(() => {
		return initCountdownGuard$.pipe(
			switchMap((nextGuardUntilOrFalse) => {
				if (nextGuardUntilOrFalse === null) {
					// Initial state - do nothing until useEffect initializes
					return of<null>(null);
				} else if (nextGuardUntilOrFalse === false) {
					// Guard is checked and inactive
					return of<false>(false).pipe(
						tap(() => setCountdownGuardValue(false))
					);
				} else {
					// Guard is active - start countdown
					return getCountdownTimer$(nextGuardUntilOrFalse.toDate()).pipe(
						map((duration) => duration.asSeconds()),
						tap((durationSeconds) => setCountdownGuardValue(durationSeconds)),
						finalize(() => setCountdownGuardValue(false))
					);
				}
			})
		);
	}, [initCountdownGuard$]);

	useSubscription(countdownGuardValue$);

	useEffect(() => {
		const oldGuardUntilRaw = guardLSManager.get();
		const oldGuardUntil = dayjs(oldGuardUntilRaw);

		/** During initialization, first we check if the persisted countdown value is a valid date in the future,
		 * which would mean the countdown guard is in progress
		 * */
		const isOldGuardValid =
			oldGuardUntil.isValid() && oldGuardUntil.isAfter(dayjs());

		if (isOldGuardValid) {
			initCountdownGuard$.next(oldGuardUntil);
		} else {
			guardLSManager.clear();

			/** Otherwise we set the guard to {false}, which indicates the LocalStorage is checked and no countdown is in progress */
			initCountdownGuard$.next(false);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	/** Force reinit of the guard */
	const reinitCountdownGuard = useCallback(() => {
		const nextCountdownGuard = dayjs().add(guardSeconds, 's');
		guardLSManager.set(nextCountdownGuard.valueOf());

		initCountdownGuard$.next(nextCountdownGuard);
	}, [guardLSManager, initCountdownGuard$, guardSeconds]);

	const resetCountdownPersistence = useCallback(() => {
		guardLSManager.clear();
	}, [guardLSManager]);

	return {
		countdownGuardValue,
		reinitCountdownGuard,
		resetCountdownPersistence,
	};
};
